import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation } from '@apollo/client';

import { type Sage100FormData } from './constants.ts';
import Sage100IntegrationForm from './Sage100IntegrationForm.tsx';
import IntegrationBreadcrumb from '../../components/IntegrationBreadcrumb.tsx';
import Layout from '../../../core/components/Layout.tsx';
import Page from '../../../core/components/Page/index.tsx';
import SettingsNavigation from '../../../core/components/SettingsNavigation.tsx';
import Button from '../../../core/components/Button.tsx';
import { useToaster } from '../../../core/components/Toast.tsx';
import { CREATE_SAGE100_INTEGRATION, GET_SAGE100_INTEGRATIONS } from '../../queries.ts';
import { redirectTo, url } from '../../../core/utils/link.ts';
import { CUSTOMER_INTEGRATION_SAGE100_PATH } from '../../constants.ts';

const FORM_ID = 'sage100-integration-add-form';

const Sage100IntegrationAddPage = () => {
    const { t } = useTranslation('integrations');
    const navigate = useNavigate();
    const { publishToast } = useToaster();
    const backTooltipText = `${t('backTooltipText')} ${t('erp.pageTitle')} / ${t('sage100.title')}`;

    const [createIntegration, { loading: isSubmitting }] = useMutation(CREATE_SAGE100_INTEGRATION, {
        refetchQueries: [GET_SAGE100_INTEGRATIONS],
    });

    const handleSubmit = async (formData: Sage100FormData) => {
        try {
            const res = await createIntegration({
                variables: {
                    name: formData.name,
                    entitlementId: formData.entitlementId,
                    dataSet: formData.dataSet,
                    salesOrderType: formData.salesOrderType,
                    authFlow: formData.authFlow,
                    orderChannels: formData.orderChannelIds,
                    rfqChannels: formData.rfqChannelIds,
                },
            });

            if (formData.authFlow === 'authorization_code' && res.data.createSage100Integration.redirectUrl) {
                redirectTo(res.data.createSage100Integration.redirectUrl);
                return;
            }

            publishToast({ description: t('sage100.createSuccess'), status: 'success' });
            navigate(url(CUSTOMER_INTEGRATION_SAGE100_PATH));
        } catch (error) {
            console.error(error);
            publishToast({ description: t('sage100.createError'), status: 'error' });
        }
    };

    return (
        <Layout>
            <SettingsNavigation />

            <Page className="flex-1">
                <Page.Header className="justify-between">
                    <IntegrationBreadcrumb
                        backTooltip={backTooltipText}
                        items={[
                            { label: t('sage100.title'), to: url(CUSTOMER_INTEGRATION_SAGE100_PATH) },
                            { label: t('sage100.form.createTitle') },
                        ]}
                    />
                    <div className="flex gap-2">
                        <Button type="button" asChild>
                            <Link to={url(CUSTOMER_INTEGRATION_SAGE100_PATH)}>{t('sage100.form.cancelButton')}</Link>
                        </Button>
                        <Button variant="primary" type="submit" form={FORM_ID} loading={isSubmitting}>
                            {t('sage100.form.createButton')}
                        </Button>
                    </div>
                </Page.Header>

                <Page.Content lowered className="flex flex-col gap-6">
                    <Sage100IntegrationForm onSubmit={handleSubmit} formId={FORM_ID} />
                </Page.Content>
            </Page>
        </Layout>
    );
};

export default Sage100IntegrationAddPage;
